import {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import {borderRadius, colors, fontSize, fontWeight, letterSpacing, lineHeight, spacing} from '@/styles/tokens';

/**
 * `neutral` states a problem or a constraint; `positive` states the answer to one.
 * The tone carries meaning, so it is never picked for looks.
 */
export type NoteBlockTone = 'neutral' | 'positive';

export interface NoteBlockProps {
    /** Small uppercase label, e.g. "The Risk" / "Our Approach". */
    label: string;
    text: string;
    tone?: NoteBlockTone;
    sx?: SxProps<Theme>;
}

const wrapBaseSx = {
    borderRadius: borderRadius.lg,
    p: spacing[16]
} as const;

const toneWrapSx: Record<NoteBlockTone, object> = {
    neutral: {bgcolor: colors.background.orangeTint},
    positive: {bgcolor: colors.background.tag}
};

const labelBaseSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.eyebrow,
    textTransform: 'uppercase' as const,
    lineHeight: lineHeight[20],
    mb: spacing[8]
} as const;

const toneLabelSx: Record<NoteBlockTone, object> = {
    neutral: {color: colors.accent.orangeDeep},
    positive: {color: colors.primary.main}
};

const textSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

/**
 * NoteBlock (Atom) — a tinted box with an uppercase label and a short body. Pairs
 * up to read as problem → answer, and works standalone as a caveat or a callout.
 *
 * Sibling of `StatCallout`, which foregrounds a figure instead of a label.
 */
const NoteBlock = ({label, text, tone = 'neutral', sx}: NoteBlockProps) => (
    <Box sx={{...wrapBaseSx, ...toneWrapSx[tone], ...sx}}>
        <Typography component="p" className="font-headers" sx={{...labelBaseSx, ...toneLabelSx[tone]}}>
            {label}
        </Typography>
        <Typography component="p" className="font-regular-text" sx={textSx}>
            {text}
        </Typography>
    </Box>
);

export default memo(NoteBlock);
